"use client";

import type { JSX } from "react";
import { motion, type Variants } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const comparisonCards = [
  {
    title: (
      <>
        Less Paperwork on{" "}
        <br className="hidden sm:block" />
        Your Side
      </>
    ),
    description:
      "Send us the required information and documents. Our team prepares the incorporation process and handles the filing work required for your setup.",
    image: "/hong-kong-company-formation-v2/group-129.png",
    imageAlt: "Group",
    imageClass: "lg:absolute lg:left-[33px] lg:-bottom-6 max-sm:h-[150px] lg:h-[206px] lg:w-[300px]",
    titleClass: "text-2xl sm:whitespace-nowrap",
  },
  {
    title: "Company, Banking and Payments Together",
    description:
      "Instead of forming a company first and then searching for banking and payment options yourself, your setup can include the accounts your business needs to receive and manage money internationally.",
    image: "/hong-kong-company-formation-v2/group-134.png",
    imageAlt: "Group",
    imageClass: "lg:absolute lg:left-[34px] lg:-bottom-6 max-sm:h-[150px] lg:h-[178px] lg:w-[305px]",
    titleClass: "text-xl",
  },
  {
    title: (
      <>
        Built for Online{" "}
        <br className="hidden sm:block" />
        Businesses
      </>
    ),
    description: (
      <>
        We have worked extensively with
        <br />
        e-commerce businesses that need more than a company certificate. Your
        setup can extend into Airwallex, PayPal, Payoneer and additional payment
        and banking options.
      </>
    ),
    image: "/home-v3/image-28.png",
    imageAlt: "Image",
    imageClass:
      "lg:absolute lg:-bottom-6 lg:-left-6 lg:left-[15px] max-sm:h-[150px] lg:h-[198.08px] lg:w-[238.1px]",
    titleClass: "text-xl sm:whitespace-nowrap",
  },
];

const cardsContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 48 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] },
  },
};

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

// The card glow (background blob + top-right shine) is a static, pre-rendered
// PNG per card per breakpoint rather than a live CSS/SVG blur. It used to be
// computed at runtime (a 137px CSS blur plus an SVG filter chain running
// feGaussianBlur + feTurbulence), which is expensive to rasterize and was
// causing scroll jank through this section — up to 3 cards' worth of large
// blur/noise filters repainting at once. The glow's position is fixed-px
// within the card, and the card is fixed-size at lg+ (392x479) and at any
// width once it saturates its 392px max-width below lg (auto height, but
// deterministic for a given width) — so one capture per state is exact.
// Below ~424px viewport the card is narrower than its max-width and the glow
// is genuinely fluid; the "fluid" capture (390px viewport) is a representative
// approximation rather than pixel-exact at every phone width.
const glowStates = [
  { name: "fluid", className: "block min-[424px]:hidden" },
  { name: "auto", className: "hidden min-[424px]:block lg:hidden" },
  { name: "fixed", className: "hidden lg:block" },
] as const;

const CardGlow = ({ cardIndex }: { cardIndex: number }): JSX.Element => (
  <>
    {glowStates.map((state) => (
      <img
        key={state.name}
        aria-hidden="true"
        alt=""
        src={`/hong-kong-company-formation-v2/generated/founder-card-${cardIndex}-${state.name}.png`}
        className={`pointer-events-none absolute inset-0 h-full w-full object-cover ${state.className}`}
      />
    ))}
  </>
);

export const FounderTrustBenefitsSection = (): JSX.Element => {
  return (

       <section id="founder-trust-benefits" className="relative my-5 sm:my-32 flex w-full flex-col items-center gap-14 px-4 pb-5 bg-black  lg:px-[108px]">
      <header className="flex w-full max-w-[1224px] flex-col items-center gap-4 text-center">
        <Badge className="animate-revolving-border h-auto rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px] backdrop-brightness-100 [-webkit-backdrop-filter:blur(3.5px)_brightness(100%)]">
          Why MuteTaxes
        </Badge>
        <motion.h2
          className="max-w-[1008px] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-[34px] font-normal leading-tight tracking-[0] text-white sm:text-[56px] sm:leading-[61px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
       Why Founders Trust MuteTaxes
        </motion.h2>
      </header>
      <motion.div
        className="relative z-0 w-full max-w-[1640px]"
        variants={cardsContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
      >
        <div className="flex w-full flex-wrap items-center justify-center gap-6">
          {comparisonCards.map((card, index) => (
            <motion.div
              key={card.image}
              variants={cardVariants}
              className="relative w-full max-w-[392px] transform-gpu will-change-transform"
            >
              <Card className="relative h-auto w-full shrink-0 overflow-hidden rounded-[32px] border-0 bg-[#0f1218] px-5 pt-5 text-white shadow-none sm:p-6 lg:h-[479px]">
                <CardGlow cardIndex={index} />
                <CardContent className="relative z-10 flex h-full flex-col p-0 lg:flex-row">
                  <div className="flex w-full flex-col items-center gap-4 text-center sm:items-start sm:text-left">
                    <h3
                      className={`mt-[-1px] [font-family:'Suisse_Intl_Trial-Bold',Helvetica] font-bold leading-7 tracking-[0] text-white ${card.titleClass}`}
                    >
                      {card.title}
                    </h3>
                    <p className="w-full max-w-[342px] [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-base font-normal leading-6 tracking-[0] text-white">
                      {card.description}
                    </p>
                  </div>
                  <img
                    className={`relative mx-auto mt-4 h-auto max-h-[200px] w-auto max-w-[85%] object-contain sm:mx-0 lg:mx-0 lg:mt-0 lg:max-h-none lg:w-auto lg:max-w-none ${card.imageClass}`}
                    alt={card.imageAlt}
                    src={card.image}
                  />
                </CardContent>
              </Card>
              {index === 0 && (
                <div className="pointer-events-none absolute -left-[105px] -bottom-[40%] z-10 h-[199px] w-[180px] -translate-y-1/2 max-sm:hidden">
                  <img
                    className="h-full w-full animate-float-reverse object-cover"
                    alt="Image"
                    src="/home-v3/image-38-1.png"
                  />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
   
    </section>
  );
};
